import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const url  = import.meta.env.VITE_SUPABASE_URL  as string | undefined;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !anon) {
  // Warn at runtime instead of throwing at module load — a module-level throw
  // crashes every lazy-loaded page that imports this file (white screen).
  console.warn('[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set. Restart the dev server after editing .env.local');
}

export const supabase = createClient<Database>(
  url  ?? 'https://placeholder.supabase.co',
  anon ?? 'placeholder',
  {
    auth: {
      persistSession:     true,
      autoRefreshToken:   true,
      detectSessionInUrl: true,
    },
  },
);

// ── Raw access token, mirrored from the Supabase Auth session ──
// supabase-js attaches the session to its own query-builder calls
// automatically. A handful of call sites still need the raw JWT string for a
// manual `Authorization` header when calling edge functions directly via
// fetch() — this keeps that string in sync without those call sites knowing
// anything about how the session is managed.
let currentAccessToken: string | null = null;
const accessTokenListeners = new Set<(token: string | null) => void>();

supabase.auth.onAuthStateChange((_event, session) => {
  currentAccessToken = session?.access_token ?? null;
  accessTokenListeners.forEach(listener => listener(currentAccessToken));
});

export function getSupabaseAccessToken() {
  return currentAccessToken;
}

export function waitForSupabaseAccessToken(timeoutMs = 10000): Promise<boolean> {
  if (currentAccessToken) return Promise.resolve(true);

  return new Promise(resolve => {
    const finish = (ready: boolean) => {
      clearTimeout(timeout);
      accessTokenListeners.delete(onTokenChange);
      resolve(ready);
    };
    const onTokenChange = (token: string | null) => {
      if (token) finish(true);
    };
    const timeout = window.setTimeout(() => finish(false), timeoutMs);
    accessTokenListeners.add(onTokenChange);
  });
}
